import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Zap, Sparkles, Star, Check, X } from "lucide-react";
import NequiButton from "@/components/NequiButton";
import WompiButton from "@/components/WompiButton";
import { IS_NEQUI_ENABLED } from "@/lib/config/featureFlags";

interface CreditPack {
  id: string;
  credits: number;
  price: number;
  label: string;
  popular?: boolean;
  savingsLabel?: string;
}

const CREDIT_PACKS: CreditPack[] = [
  {
    id: "topup_30",
    credits: 30,
    price: 14900,
    label: "Básico",
  },
  {
    id: "topup_80",
    credits: 80,
    price: 34900,
    label: "Popular",
    popular: true,
    savingsLabel: "Ahorra 12%",
  },
  {
    id: "topup_200",
    credits: 200,
    price: 74900,
    label: "Premium",
    savingsLabel: "Ahorra 25%",
  },
];

import { IS_CREDIT_RECHARGE_ENABLED } from "@/lib/config/featureFlags";

interface CreditRechargeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreditRechargeModal: React.FC<CreditRechargeModalProps> = ({
  isOpen,
  onClose,
}) => {
  if (!IS_CREDIT_RECHARGE_ENABLED) return null;

  const [selectedPack, setSelectedPack] = useState<CreditPack | null>(
    CREDIT_PACKS[1] // Default to "Popular"
  );

  const handleSuccess = () => {
    // Dispatch credits-updated to refresh CreditBadge
    window.dispatchEvent(new CustomEvent("credits-updated"));
    // Close after a small delay so user sees the success state
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const pricePerCredit = (pack: CreditPack) => {
    return Math.round(pack.price / pack.credits);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg gap-0 p-0 overflow-hidden border-none shadow-2xl rounded-3xl bg-white dark:bg-gray-950">
        {/* Header Gradient */}
        <div className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 px-6 py-5 text-white overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZG90cyIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48Y2lyY2xlIGN4PSIxMCIgY3k9IjEwIiByPSIxLjUiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9InVybCgjZG90cykiLz48L3N2Zz4=')] opacity-50" />
          <div className="relative z-10">
            <button
              type="button"
              onClick={onClose}
              className="absolute -top-1 right-0 p-1.5 bg-white/20 hover:bg-white/30 rounded-full transition-colors backdrop-blur-sm"
              aria-label="Cerrar modal"
            >
              <X className="w-4 h-4" />
            </button>
            <DialogHeader className="text-left space-y-1">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Zap className="w-5 h-5 fill-white" />
                </div>
                <DialogTitle className="text-xl font-bold text-white">
                  Recargar Créditos IA
                </DialogTitle>
              </div>
              <DialogDescription className="text-white/80 text-sm pl-[42px]">
                Compra créditos adicionales para seguir usando SarAI sin límites
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {/* Pack Cards */}
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {CREDIT_PACKS.map((pack) => {
              const isSelected = selectedPack?.id === pack.id;

              return (
                <button
                  key={pack.id}
                  type="button"
                  onClick={() => setSelectedPack(pack)}
                  className={`relative flex flex-col items-center p-4 rounded-2xl border-2 transition-all duration-200 text-center group hover:shadow-lg ${
                    isSelected
                      ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30 shadow-md ring-1 ring-amber-400/30"
                      : "border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-amber-300"
                  }`}
                >
                  {/* Popular Badge */}
                  {pack.popular && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[9px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 shadow-sm">
                      <Star className="w-2.5 h-2.5 fill-current" />
                      MÁS POPULAR
                    </div>
                  )}

                  {/* Savings Badge */}
                  {pack.savingsLabel && (
                    <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 rounded-full mb-1.5">
                      {pack.savingsLabel}
                    </span>
                  )}
                  {!pack.savingsLabel && <div className="h-[22px] mb-1.5" />}

                  {/* Credits Amount */}
                  <div className="flex items-center gap-1 mb-1">
                    <Sparkles className={`w-4 h-4 ${isSelected ? "text-amber-500" : "text-gray-400"} transition-colors`} />
                    <span className="text-2xl font-black text-gray-900 dark:text-white">
                      {pack.credits}
                    </span>
                  </div>
                  <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2.5">
                    créditos
                  </span>

                  {/* Price */}
                  <div className="text-base font-bold text-gray-900 dark:text-white">
                    ${pack.price.toLocaleString("es-CO")}
                  </div>
                  <span className="text-[10px] text-gray-400 mt-0.5">
                    ${pricePerCredit(pack).toLocaleString("es-CO")} / crédito
                  </span>

                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white stroke-[3]" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Feature list */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-3 border border-gray-100 dark:border-gray-800">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
              Con cada recarga obtienes:
            </p>
            <ul className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
              <li className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
                  <Check className="w-2.5 h-2.5 text-emerald-600 stroke-[3]" />
                </div>
                Créditos que no expiran hasta usarlos
              </li>
              <li className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
                  <Check className="w-2.5 h-2.5 text-emerald-600 stroke-[3]" />
                </div>
                Se suman a tus créditos del plan mensual
              </li>
              <li className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
                  <Check className="w-2.5 h-2.5 text-emerald-600 stroke-[3]" />
                </div>
                Disponibles inmediatamente después del pago
              </li>
            </ul>
          </div>

          {/* Payment Button */}
          {selectedPack && (
            <div className="space-y-2">
              {IS_NEQUI_ENABLED ? (
                <NequiButton
                  amount={selectedPack.price}
                  planId={selectedPack.id}
                  planName={`Recarga ${selectedPack.credits} créditos`}
                  buttonText={`Pagar $${selectedPack.price.toLocaleString("es-CO")} con Nequi`}
                  className="w-full h-12 text-sm"
                  onSuccess={handleSuccess}
                />
              ) : (
                <WompiButton
                  amount={selectedPack.price}
                  planId={selectedPack.id}
                  planName={`Recarga ${selectedPack.credits} créditos`}
                  buttonText={`Pagar $${selectedPack.price.toLocaleString("es-CO")} con Wompi`}
                  className="w-full h-12 text-sm font-bold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  onSuccess={handleSuccess}
                />
              )}
              <p className="text-[10px] text-center text-gray-400">
                Pago seguro procesado por Wompi · IVA incluido
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreditRechargeModal;
